import { getUndoCheckoutSuccessMessage } from "../constants/messageTypes";
import {
  ON_ERROR_MESSAGE_INVOKE,
  ON_SUCCESS_MESSAGE_INVOKE,
} from "../constants/actionTypes";
import { ERROR_TYPE, SUCCESS_TYPE } from "../constants/messageTypes";
import {
  toastPolicyByTypePrefix,
  toastPolicyDefaults,
} from "../toast/toastPolicy";
import { CHECKOUT_STATUS } from "../constants/metaData";
import {
  CHECKOUT_ERROR_MESSAGE,
  CHECKOUT_ONEDRIVE_UPLOAD_FAILED_MESSAGE,
  CHECKOUT_SUCCESS_MESSAGE,
  UNDO_CHECKOUT_ERROR_MESSAGE,
  UNDO_CHECKOUT_SUCCESS_MESSAGE,
  DocumentDelete,
} from "../constants/messageTypes";

const THUNK_PHASES = ["/pending", "/fulfilled", "/rejected"];

function parseThunkActionType(type = "") {
  for (const phase of THUNK_PHASES) {
    if (type.endsWith(phase)) {
      return {
        typePrefix: type.slice(0, -phase.length),
        phase: phase.slice(1),
      };
    }
  }
  return null;
}

function extractRequestToastOverrides(arg) {
  if (!arg || typeof arg !== "object") {
    return {};
  }

  // Allow either arg.toast or arg.__toast to avoid naming conflicts.
  const toast = arg.toast ?? arg.__toast;
  if (!toast || typeof toast !== "object") {
    return {};
  }
  return toast;
}

function getErrorDescription(action, fallback) {
  const payload = action?.payload;
  const error = action?.error;

  if (typeof payload === "string" && payload.trim()) {
    return payload;
  }

  let payloadMessage =
    payload?.userMessage ??
    payload?.errorMessage ??
    payload?.message ??
    payload?.data ??
    payload?.title;

  if (payloadMessage && typeof payloadMessage === "object") {
    payloadMessage = payloadMessage.Exception || 
                     (payloadMessage.Messages && Array.isArray(payloadMessage.Messages) ? payloadMessage.Messages.join(", ") : "") ||
                     payloadMessage.message || 
                     payloadMessage.detail || 
                     payloadMessage.title || 
                     payloadMessage.error || 
                     payloadMessage.Error || 
                     "";
  }

  if (typeof payloadMessage === "string" && payloadMessage.trim()) {
    return payloadMessage;
  }

  const errorMessage = error?.message;
  if (typeof errorMessage === "string" && errorMessage.trim()) {
    return errorMessage;
  }

  return fallback;
}

function getCheckoutDocumentTitle(action) {
  const firstDocument = action?.meta?.arg?.checkoutList?.[0];
  const rawTitle = firstDocument?.Title ?? firstDocument?.title ?? "";

  if (!rawTitle) {
    return "";
  }

  try {
    return decodeURIComponent(String(rawTitle));
  } catch {
    return String(rawTitle);
  }
}

function getCheckinDocumentTitle(action, typePrefix) {
  let rawTitle = "";

  if (typePrefix === "checkinApi/checkinDocument") {
    rawTitle = action?.meta?.arg?.docTitle ?? "";
  } else if (typePrefix === "documents/checkinExistingFlow/perform") {
    rawTitle = action?.meta?.arg?.documentRevision?.Title ?? "";
  } else if (typePrefix === "checkinNewRevision/post") {
    rawTitle = action?.meta?.arg?.selectedRevisionItem?.title ?? "";
  }

  if (!rawTitle) {
    return "";
  }

  try {
    return decodeURIComponent(String(rawTitle));
  } catch {
    return String(rawTitle);
  }
}

function shouldHandle(policy, requestOverrides) {
  // Explicit per-request suppression always wins.
  if (requestOverrides?.suppressToast === true) {
    return false;
  }

  // No policy and no per-request show flags => do not auto-handle.
  if (
    !policy &&
    requestOverrides?.showSuccess === undefined &&
    requestOverrides?.showError === undefined
  ) {
    return false;
  }

  return true;
}

function isInternalCheckinDownload(action, typePrefix) {
  if (typePrefix !== "oneDriveDownload/fetch") {
    return false;
  }

  const operationType = String(action?.meta?.arg?.operationType ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

  // Check-in flows use the same download thunk as an internal step.
  // We suppress auto-toast there to avoid duplicate/noisy toasts.
  return (
    operationType === "checkinnew" || operationType === "checkinnewrevision"
  );
}

export const toastMiddleware = () => (next) => (action) => {
  const result = next(action);

  if (!action?.type || !action?.meta?.requestId) {
    return result;
  }

  const parsed = parseThunkActionType(action.type);
  if (!parsed) {
    return result;
  }

  // Never show toast for OneDrive download URL fetch endpoint.
  // This API is used as an internal helper in multiple flows.
  if (parsed.typePrefix === "oneDriveDownload/fetch") {
    return result;
  }

  const policy = toastPolicyByTypePrefix[parsed.typePrefix];
  const requestOverrides = extractRequestToastOverrides(action.meta?.arg);

  if (isInternalCheckinDownload(action, parsed.typePrefix)) {
    return result;
  }

  if (!shouldHandle(policy, requestOverrides)) {
    return result;
  }

  const config = {
    ...toastPolicyDefaults,
    ...(policy ?? {}),
    ...requestOverrides,
  };

  // Checkout thunk uses the same endpoint for both Checkout and Undo Checkout.
  // Make toast messages reflect the operation.
  if (parsed.typePrefix === "documents/documentCheckoutFlow/performCheckout") {
    const operationStatus = action?.meta?.arg?.operationStatus;
    const isUndo = operationStatus === CHECKOUT_STATUS.undoCheckout;
    const documentTitle = getCheckoutDocumentTitle(action);

    if (parsed.phase === "fulfilled") {
      if (isUndo) {
        config.successTitle = "Undo check-out completed";
        config.successMessage = getUndoCheckoutSuccessMessage(documentTitle);
      } else {
        config.successTitle = "Check-out completed";
        config.successMessage = documentTitle
          ? `${documentTitle} checked out successfully!`
          : CHECKOUT_SUCCESS_MESSAGE;
      }
    }

    if (parsed.phase === "rejected") {
      if (isUndo) {
        config.errorTitle = "Undo checkout failed";
        config.errorMessage = UNDO_CHECKOUT_ERROR_MESSAGE;
      } else {
        const payload = action?.payload;
        const ifsCheckedOut =
          payload != null &&
          typeof payload === "object" &&
          payload.checkoutSucceededIfs === true;
        config.errorTitle = ifsCheckedOut
          ? "Working Documents copy failed"
          : "Checkout failed";
        if (typeof payload?.message === "string" && payload.message.trim()) {
          config.errorMessage = payload.message.trim();
        } else {
          config.errorMessage = CHECKOUT_ERROR_MESSAGE;
        }
      }
    }
  }

  if (
    (parsed.typePrefix === "checkinApi/checkinDocument" ||
      parsed.typePrefix === "documents/checkinExistingFlow/perform" ||
      parsed.typePrefix === "checkinNewRevision/post") &&
    parsed.phase === "fulfilled"
  ) {
    const documentTitle = getCheckinDocumentTitle(action, parsed.typePrefix);
    if (documentTitle) {
      config.successMessage = `${documentTitle} checked in successfully!`;
    }
  }

  if (
    parsed.typePrefix === "documents/deleteWorkingDocument" &&
    parsed.phase === "fulfilled"
  ) {
    const displayName = action?.payload?.documentDisplayName ?? "Document";
    config.successTitle = "Document deleted";
    config.successMessage = DocumentDelete(displayName);
  }

  // URL fetch calls are often internal flow steps; never show success toast for them.
  if (
    parsed.typePrefix === "oneDriveDownload/fetch" &&
    parsed.phase === "fulfilled"
  ) {
    return result;
  }

  if (parsed.phase === "fulfilled" && config.showSuccess) {
    // In this system, status 202 (Accepted) often represents an "IFS Bad Request"
    // Also, if the payload data is a string, it's likely an error message returned by the backend.
    if (
      action.payload?.status === 202 ||
      typeof action.payload?.data === "string"
    ) {
      return result;
    }

    next({
      type: ON_SUCCESS_MESSAGE_INVOKE,
      payload: {
        type: SUCCESS_TYPE,
        title: config.successTitle,
        message: config.successMessage,
      },
    });
    return result;
  }

  if (parsed.phase === "rejected" && config.showError) {
    next({
      type: ON_ERROR_MESSAGE_INVOKE,
      payload: {
        type: ERROR_TYPE,
        title: config.errorTitle,
        message: config.errorMessage,
        errorMessage: getErrorDescription(action, config.errorMessage),
        domain: config.domain,
      },
    });
  }

  return result;
};
